import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
// Icons - only import those used in Index.tsx directly (Step 1 + Step 6 + legacy)
import { Sparkles, Palette, Video, Package, Check, Gift, Keyboard, MessageCircle, FileImage, FileText, Droplets, Type, Layers, Lightbulb, RefreshCw, CheckCircle, LockOpen, Award, ArrowRight, Download, Copy, Loader2, LogIn, LogOut, ChevronDown, Folder, Zap, BadgeCheck, ArrowLeft, ExternalLink, Building2, Rocket } from "lucide-react";
import { ColorSwatch } from "@/components/ColorSwatch";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import PromoBanner from "@/components/PromoBanner";
import { SocialProof } from "@/components/SocialProof";
import { LogoGallery } from "@/components/LogoGallery";
import { FAQ } from "@/components/FAQ";
import Footer from "@/components/Footer";
import { LoadingMessages } from "@/components/LoadingMessages";
import { SignupModal } from "@/components/SignupModal";
import { PaywallModal } from "@/components/PaywallModal";
import HardPaywallModal from "@/components/HardPaywallModal";
import { type LogoOption } from "@/components/TripleLogoSelector";
import { useGuestGeneration } from "@/hooks/useGuestGeneration";
import { useWizardPersistence } from "@/hooks/useWizardPersistence";
import { useDynamicFont } from "@/hooks/useDynamicFont";
import { useRecreationCredits } from "@/hooks/useRecreationCredits";
import { useScrollReveal } from "@/contexts/ScrollRevealContext";
import marcaClickLogos from "@/assets/marca-clicklogos.svg";
import brandGuidelinesPreview from "@/assets/showcase/brand-guidelines-preview.webp";
import mascotesPreview from "@/assets/showcase/mascotes-preview.webp";
import { trackTikTokViewContent } from "@/lib/tiktok-tracking";
import { trackLead, trackInitiateCheckout } from "@/lib/unified-tracking";
import { useUtmCapture } from "@/hooks/useUtmTracking";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { usePurchaseStatus } from "@/hooks/usePurchaseStatus";
import { useAuth } from "@/contexts/AuthContext";
import { getFontDownloadLink } from "@/lib/fontOptions";
import { uploadLogoVariations } from "@/lib/uploadDataUrl";

// Lazy load below-fold sections for better performance
const WhatYouGetSection = lazy(() => import("@/components/sections/WhatYouGetSection").then(m => ({ default: m.WhatYouGetSection })));
const ProductsSection = lazy(() => import("@/components/sections/ProductsSection").then(m => ({ default: m.ProductsSection })));

// Lazy load wizard steps for reduced initial bundle (Code Split Phase 1)
const Step2Personality = lazy(() => import("@/components/wizard/Step2Personality"));
const Step3Colors = lazy(() => import("@/components/wizard/Step3Colors"));
const Step4Concept = lazy(() => import("@/components/wizard/Step4Concept"));
const Step5Approval = lazy(() => import("@/components/wizard/Step5Approval"));

// Wizard Data
const SEGMENTS = [
  "Beleza e Estética",
  "Vestuário e Moda",
  "Construção Civil",
  "Alimentação e Bebidas",
  "Serviços Domésticos",
  "Transporte e Logística",
  "Mercados e Mercearias",
  "Educação e Treinamentos",
  "Marketing e Digital",
  "Artesanato e Manufatura",
  "Tecnologia",
  "Saúde e Bem-estar",
  "Imóveis",
  "Finanças",
  "Entretenimento",
  "Serviços Gerais",
];

// PERSONALITIES and SYMBOL_CONCEPTS moved to lazy-loaded wizard step components

// Import COLOR_VIBES from Step3Colors for semantic color data
import { COLOR_VIBES } from "@/components/wizard/Step3Colors";

// Legacy alias for backwards compatibility
const COLORS = COLOR_VIBES;

interface FormData {
  brandName: string;
  segment: string;
  description: string;
  personality: string;
  color: string;
  symbolConcept: string;
  conceptRefinement: string;
}

const Index = () => {
  // Capture UTM params on page load
  useUtmCapture();
  const navigate = useNavigate();

  // Guest generation tracking (for paywall)
  const { hasGeneratedPreview, markGenerated, getSessionId } = useGuestGeneration();

  // Wizard persistence (for OAuth redirect protection)
  const { 
    saveWizardState, 
    restoreWizardState, 
    clearWizardState, 
    markOAuthPending, 
    wasOAuthPending, 
    clearOAuthPending 
  } = useWizardPersistence();

  // Track if we've already restored state (prevent double restore)
  const hasRestoredRef = useRef(false);

  // Generation limits
  const MAX_GENERATIONS = 10; // 1 initial + 9 variations
  
  // Wizard state
  const [currentStep, setCurrentStep] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationCount, setGenerationCount] = useState(0);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showHardPaywall, setShowHardPaywall] = useState(false);
  const [pendingDownload, setPendingDownload] = useState<ReturnType<typeof getLogoVariations>[0] | null>(null);
  
  // NEW: Approval Step states
  const [rawLogoUrl, setRawLogoUrl] = useState<string | null>(null);
  const [logoOptions, setLogoOptions] = useState<LogoOption[]>([]);
  const [selectedLogoId, setSelectedLogoId] = useState<number | null>(null);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [pendingApprovalAction, setPendingApprovalAction] = useState<'approve' | 'regenerate' | null>(null);
  const [isFinalizingBrand, setIsFinalizingBrand] = useState(false);
  const [isRecreating, setIsRecreating] = useState(false);
  
  // 🆕 Saved brand kit ID for uploading variations
  const [savedBrandKitId, setSavedBrandKitId] = useState<string | null>(null);
  const [variationsUploaded, setVariationsUploaded] = useState(false);
  
  // AI Suggestion state moved to Step4Concept component
  
  // 🆕 Cor calculada pela IA (sincronização paleta/logo)
  const [calculatedPrimaryColor, setCalculatedPrimaryColor] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<FormData>({
    brandName: "",
    segment: "",
    description: "",
    personality: "",
    color: "",
    symbolConcept: "",
    conceptRefinement: "",
  });

  const totalSteps = 6; // 1-4 wizard, 5 approval, 6 result

  // Auth from global context (handles OAuth persistence)
  const { session, user, signOut, isAuthReady } = useAuth();
  const isCheckingAuth = !isAuthReady; // Alias for existing UI code

  // Purchase status hook - checks if user has paid
  const { hasPurchased, isLoading: purchaseLoading, refetchStatus } = usePurchaseStatus('brand_kit');

  // 🆕 Recreation credits hook
  const { state: recreationState, useCredit, refetch: refetchCredits } = useRecreationCredits();

  // =============================================================
  // WIZARD STATE RESTORATION (handles OAuth redirect return)
  // =============================================================
  useEffect(() => {
    // GATE 1: Wait for auth to be ready before processing
    if (!isAuthReady) return;
    
    // GATE 2: Only run once
    if (hasRestoredRef.current) return;
    hasRestoredRef.current = true;

    const savedState = restoreWizardState();
    const oauthWasPending = wasOAuthPending();

    if (savedState) {
      console.log('[WizardRestore] Found saved state at step', savedState.currentStep);
      
      // Restore form data (always, regardless of auth)
      setFormData(savedState.formData);
      setCurrentStep(savedState.currentStep);
      setGenerationCount(savedState.generationCount || 0);
      
      // Restore generated brand if it exists
      if (savedState.generatedBrand) {
        setGeneratedBrand(savedState.generatedBrand);
      }

      // Restore rawLogoUrl if it exists (critical for Step 5)
      if (savedState.rawLogoUrl) {
        setRawLogoUrl(savedState.rawLogoUrl);
        console.log('[WizardRestore] Restored rawLogoUrl');
      }

      // GATE 3: Only show success toast if returning from OAuth AND session exists
      if (oauthWasPending) {
        clearOAuthPending();
        
        if (session) {
          // ✅ Auth confirmed - show success
          toast.success('Sua marca foi restaurada!');
        } else {
          // ⚠️ OAuth was initiated but no session - show warning
          console.warn('[WizardRestore] OAuth was pending but no session established');
          toast.error('Sessão não estabelecida. Faça login novamente.');
        }
      }
    } else if (oauthWasPending) {
      // Clear stale OAuth flag even if no saved state
      clearOAuthPending();
    }
  }, [isAuthReady, session, restoreWizardState, wasOAuthPending, clearOAuthPending]);

  // =============================================================
  // AUTO-SAVE WIZARD STATE (protects against accidental loss)
  // =============================================================
  useEffect(() => {
    // Only save if user has started the wizard (has brand name or moved past step 1)
    if (currentStep > 1 || formData.brandName.trim() !== '') {
      // Need to read generatedBrand after it's defined, so we use a callback pattern
      const timeoutId = setTimeout(() => {
        // Get current generatedBrand value (will be null initially)
        const currentGeneratedBrand = (window as any).__clicklogos_generatedBrand || null;
        saveWizardState(formData, currentStep, currentGeneratedBrand, generationCount, rawLogoUrl);
      }, 500); // Debounce to avoid excessive saves
      
      return () => clearTimeout(timeoutId);
    }
  }, [currentStep, formData, generationCount, rawLogoUrl, saveWizardState]);

  // Use React-based scroll reveal hook (fixes forced reflow)
  const { registerElement } = useScrollReveal();

  // Logout function
  const handleLogout = async () => {
    await signOut();
    
    // Reset wizard state completely
    setCurrentStep(1);
    setGeneratedBrand(null);
    setFormData({
      brandName: '',
      segment: '',
      description: '',
      personality: '',
      color: '',
      symbolConcept: '',
      conceptRefinement: '',
    });
    clearWizardState();
    
    toast.success("Logout realizado com sucesso!");
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Reset wizard to step 1
  const handleResetWizard = () => {
    setCurrentStep(1);
    setGeneratedBrand(null);
    setFormData({
      brandName: '',
      segment: '',
      description: '',
      personality: '',
      color: '',
      symbolConcept: '',
      conceptRefinement: '',
    });
    clearWizardState();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Track ViewContent when landing page loads (Fix C: defer to idle time)
  useEffect(() => {
    const trackViewContent = () => {
      trackTikTokViewContent({
        content_id: 'clicklogos-completo',
        content_name: 'ClickLogos Completo',
        content_type: 'product',
        value: 100,
        currency: 'BRL',
        brand: 'ClickLogos'
      });
    };
    
    // Defer tracking to idle time for better LCP/TTI
    if ('requestIdleCallback' in window) {
      (window as Window).requestIdleCallback(trackViewContent, { timeout: 3000 });
    } else {
      setTimeout(trackViewContent, 100);
    }
  }, []);

  const updateFormData = (key: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.brandName.trim() !== "" && formData.segment !== "" && formData.description.trim() !== "";
      case 2:
        return formData.personality !== "";
      case 3:
        return formData.color !== "";
      case 4:
        return formData.symbolConcept !== "";
      default:
        return true;
    }
  };

  const handleNext = async () => {
    if (currentStep < 4) {
      setCurrentStep((prev) => prev + 1);
    } else if (currentStep === 4) {
      // 🔐 STRICT GATE: Check if user already generated once (and hasn't paid)
      if (hasGeneratedPreview() && !hasPurchased) {
        trackInitiateCheckout({ value: 89.90 });
        setShowHardPaywall(true);
        return; // BLOCK execution
      }

      // 🔐 PAID USER LIMIT: Check if paid user has exceeded 10 generations
      if (hasPurchased && generationCount >= MAX_GENERATIONS) {
        toast.error(`Limite de ${MAX_GENERATIONS} gerações atingido. Entre em contato para mais.`);
        return; // BLOCK execution
      }

      setIsGenerating(true);
      setIsLoadingOptions(true);
      setLogoOptions([]);
      setSelectedLogoId(null);
      
      // Go to Step 5 immediately to show loading state
      setCurrentStep(5);
      
      try {
        const selectedColor = COLOR_VIBES.find(c => c.id === formData.color);
        
        // Track Lead - user is generating brand (Meta + TikTok)
        trackLead(`${formData.brandName} - ${formData.segment}`);
        
        // 🆕 TRIPLE LOGO GENERATION: Call with semantic colorVibe object
        const response = await supabase.functions.invoke('generate-raw-logo', {
          body: {
            brandName: formData.brandName,
            segment: formData.segment,
            description: formData.description,
            personality: formData.personality,
            // 🎨 AI COLOR REVOLUTION: Send semantic color data, not just HEX
            colorVibe: selectedColor ? {
              id: selectedColor.id,
              name: selectedColor.name,
              vibe: selectedColor.vibe,
              vibeEn: selectedColor.vibeEn,
              referenceHex: selectedColor.hex,
            } : { id: "verde", name: "Verde", vibe: "Natural", vibeEn: "Fresh Natural Green", referenceHex: "#22C55E" },
            symbolConcept: formData.symbolConcept,
            conceptRefinement: formData.conceptRefinement,
            count: 3,
          },
          headers: {
            'x-session-id': getSessionId(),
          }
        });

        if (response.error) {
          throw new Error(response.error.message);
        }

        if (!response.data?.success) {
          throw new Error(response.data?.error || 'Falha ao gerar logos');
        }

        // Store logo options for selection
        const options = response.data.logoOptions || [];
        setLogoOptions(options);
        
        // Set first option as rawLogoUrl for backwards compatibility
        if (options.length > 0) {
          setRawLogoUrl(options[0].rawImageUrl);
        }
        
        // Capture calculated color
        if (response.data.calculatedPrimaryColor) {
          setCalculatedPrimaryColor(response.data.calculatedPrimaryColor);
        }
        
        // Mark as generated for paywall tracking
        markGenerated(options[0]?.rawImageUrl || '', response.data);
        
        // Increment generation counter
        setGenerationCount(prev => prev + 1);
      } catch (error) {
        console.error('Error generating logo:', error);
        const errorMessage = error instanceof Error ? error.message : '';
        if (errorMessage.includes('Rate limit')) {
          toast.error("Limite de requisições atingido. Aguarde um momento.");
          return;
        }
        toast.error("Erro ao gerar logos. Tente novamente.");
        setCurrentStep(4); // Go back on error
      } finally {
        setIsGenerating(false);
        setIsLoadingOptions(false);
      }
    }
  };

  // 🆕 APPROVAL STEP: Handle approve/regenerate actions with paywall
  const handleApprovalAction = async (action: 'approve' | 'regenerate') => {
    // BARREIRA 1: Login obrigatório
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    if (!currentUser) {
      setPendingApprovalAction(action);
      setShowSignupModal(true);
      return;
    }

    // BARREIRA 2: Verificar admin bypass OU créditos
    const { data: roleData } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', currentUser.id)
      .eq('role', 'admin')
      .maybeSingle();

    const isAdmin = !!roleData;

    if (!isAdmin) {
      // Não é admin - verificar pagamento/créditos
      if (!hasPurchased) {
        trackInitiateCheckout({ value: 89.90 });
        setPendingApprovalAction(action);
        setShowPaywall(true);
        return;
      }
    }

    // ✅ PASSOU TODAS AS BARREIRAS - executar ação
    if (action === 'approve') {
      await executeFinalization();
    } else {
      await executeRegeneration();
    }
  };

  // 🆕 FINALIZE: Call expensive operations ONLY after payment verification
  const executeFinalization = async () => {
    if (!rawLogoUrl) {
      toast.error("Nenhuma logo para finalizar");
      return;
    }

    setIsFinalizingBrand(true);
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      
      if (!currentSession) {
        toast.error("Sessão expirada. Faça login novamente.");
        setShowSignupModal(true);
        return;
      }

      const selectedColor = COLORS.find(c => c.id === formData.color);
      
      // 🆕 DEBUG: Log de sincronização de cor
      console.log('🎨 SYNC CHECK:', {
        corOriginal: selectedColor?.hex,
        corCalculada: calculatedPrimaryColor,
        enviandoPara: 'finalize-branding'
      });
      
      // 🔥 EXPENSIVE OPERATION: Background removal + fonts + palette + copywriting
      const response = await supabase.functions.invoke('finalize-branding', {
        body: {
          rawImageUrl: rawLogoUrl,
          brandData: {
            brandName: formData.brandName,
            segment: formData.segment,
            description: formData.description,
            personality: formData.personality,
            color: selectedColor?.hex || "#10B981",
            symbolConcept: formData.symbolConcept,
            calculatedPrimaryColor: calculatedPrimaryColor, // 🆕 Cor calculada pela IA
          }
        }
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      if (!response.data?.success) {
        // Handle NO_CREDITS error
        if (response.data?.error === 'NO_CREDITS') {
          toast.error("Você não tem créditos disponíveis");
          setShowPaywall(true);
          return;
        }
        throw new Error(response.data?.error || 'Falha ao finalizar marca');
      }

      // ✅ SUCCESS: Set brand data and go to results
      setGeneratedBrand(response.data);
      
      // 🆕 SALVAR NO BANCO DE DADOS
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        console.log("🛑 TENTANDO SALVAR PROJETO...", {
          user_id: user.id,
          brandName: formData.brandName,
        });
        
        // 1. Create brand record
        const { data: brandRecord, error: brandError } = await supabase
          .from('brands')
          .insert({
            name: formData.brandName,
            user_id: user.id,
            logo_png_url: response.data.logo?.transparentUrl || response.data.logo?.url,
          })
          .select()
          .single();

        if (brandError) {
          console.error('❌ ERRO AO SALVAR BRAND:', brandError.message, brandError.details, brandError.hint);
          toast.error("Erro ao salvar marca: " + brandError.message);
        } else if (brandRecord) {
          console.log("✅ BRAND SALVO:", brandRecord.id);
          
          // 2. Create brand_kit record
          console.log("📝 COPYWRITING FROM AI:", response.data.copywriting);
          
          const { data: kitData, error: kitError } = await supabase
            .from('brand_kits')
            .insert({
              brand_id: brandRecord.id,
              brand_name: formData.brandName,
              // Usar tagline gerado pela IA
              brand_slogan: response.data.copywriting?.tagline || null,
              // Bio e Tagline gerados pela IA
              bio: response.data.copywriting?.bio || null,
              tagline: response.data.copywriting?.tagline || null,
              status: 'completed',
              current_step: 7,
              logo_symbol_url: response.data.logo?.transparentUrl,
              logo_color_url: response.data.logo?.url,
              color_palette: {
                primary: response.data.colorPalette?.[0]?.hex || '#10B981',
                secondary: response.data.colorPalette?.[1]?.hex || '#1F2937',
                neutral_light: response.data.colorPalette?.[2]?.hex || '#F9FAFB',
                neutral_dark: response.data.colorPalette?.[3]?.hex || '#111827',
                accent1: response.data.colorPalette?.[4]?.hex || '#F97316',
                accent2: response.data.colorPalette?.[5]?.hex || '#EC4899',
              },
              display_font_name: response.data.fontKit?.display?.name,
              display_font_url: response.data.fontKit?.display?.downloadLink,
              heading_font_name: response.data.fontKit?.titles?.name,
              heading_font_url: response.data.fontKit?.titles?.downloadLink,
              body_font_name: response.data.fontKit?.body?.name,
              body_font_url: response.data.fontKit?.body?.downloadLink,
            })
            .select('id')
            .single();

          if (kitError) {
            console.error('❌ ERRO AO SALVAR BRAND_KIT:', kitError.message, kitError.details, kitError.hint);
            toast.error("Erro ao salvar kit da marca: " + kitError.message);
          } else if (kitData) {
            console.log("✅ BRAND_KIT SALVO:", kitData.id);
            // 🆕 Save kit ID for variation upload
            setSavedBrandKitId(kitData.id);
            setVariationsUploaded(false);
            
            // 3. Create brand_profile record (non-critical)
            const selectedColor = COLORS.find(c => c.id === formData.color);
            const { error: profileError } = await supabase
              .from('brand_profiles')
              .insert({
                brand_id: brandRecord.id,
                sector: formData.segment,
                brand_personality: formData.personality ? [formData.personality] : [],
                preferred_colors: selectedColor ? [selectedColor.hex] : [],
                offer_summary: formData.description || null,
              });

            if (profileError) {
              console.error('⚠️ ERRO AO SALVAR PROFILE (não crítico):', profileError.message);
            } else {
              console.log("✅ BRAND_PROFILE SALVO");
            }
            
            toast.success("Marca salva com sucesso!");
          }
        }
      } else {
        console.log("⚠️ Usuário não logado - projeto não salvo no banco");
      }
      
      setCurrentStep(6); // Go to RESULTS (Step 6)
      toast.success("Identidade visual criada com sucesso!");
    } catch (error) {
      console.error('Error finalizing brand:', error);
      toast.error("Erro ao criar identidade visual. Tente novamente.");
    } finally {
      setIsFinalizingBrand(false);
    }
  };

  // 🆕 REGENERATE: Generate new logo variation with recreation credits
  const executeRegeneration = async () => {
    // Check recreation credits
    if (recreationState.remaining === 0) {
      if (recreationState.tier === 'guest') {
        trackInitiateCheckout({ value: 59.90 });
        setShowHardPaywall(true);
      } else {
        trackInitiateCheckout({ value: 59.90 });
        setShowPaywall(true);
      }
      return;
    }

    setIsRecreating(true);
    try {
      const selectedColor = COLORS.find(c => c.id === formData.color);
      
      // 🆕 Use a recreation credit
      const creditUsed = await useCredit();
      if (!creditUsed) {
        toast.error("Sem créditos de recreação disponíveis.");
        return;
      }
      
      const response = await supabase.functions.invoke('generate-raw-logo', {
        body: {
          brandName: formData.brandName,
          segment: formData.segment,
          description: formData.description,
          personality: formData.personality,
          color: selectedColor?.hex || "#10B981",
          symbolConcept: formData.symbolConcept,
          conceptRefinement: formData.conceptRefinement,
          count: 3,
        },
        headers: {
          'x-session-id': getSessionId(),
        }
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      if (!response.data?.success) {
        throw new Error(response.data?.error || 'Falha ao gerar novas opções');
      }

      // Update logo options
      const options = response.data.logoOptions || [];
      setLogoOptions(options);
      setSelectedLogoId(null);
      
      if (options.length > 0) {
        setRawLogoUrl(options[0].rawImageUrl);
      }
      
      // Capture calculated color
      if (response.data.calculatedPrimaryColor) {
        setCalculatedPrimaryColor(response.data.calculatedPrimaryColor);
      }
      
      setGenerationCount(prev => prev + 1);
      refetchCredits(); // Update credit state
      
      toast.success(`Novas opções geradas! (${recreationState.remaining - 1} recreações restantes)`);
    } catch (error) {
      console.error('Error regenerating logo:', error);
      toast.error("Erro ao gerar novas opções. Tente novamente.");
    } finally {
      setIsRecreating(false);
    }
  };

  // Handle login from hard paywall
  const handleLoginFromHardPaywall = () => {
    setShowHardPaywall(false);
    setShowSignupModal(true);
  };

  // Scroll to top when reaching Step 5 (approval) or Step 6 (results)
  useEffect(() => {
    if (currentStep === 5 || currentStep === 6) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [currentStep]);

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  // Logo variations array - uses PREVIEW URLs (with burned watermark) for display
  const getLogoVariations = () => [
    {
      id: 'colorida',
      label: 'Colorida',
      previewUrl: variations['colorida']?.previewUrl || transparentUrl || generatedBrand?.logo?.url,
      cleanUrl: variations['colorida']?.cleanUrl || transparentUrl || generatedBrand?.logo?.url,
      bgColor: undefined,
      checkerboard: true,
      filter: 'none' as const,
    },
    {
      id: 'preto',
      label: 'Preto 100%',
      previewUrl: variations['preto']?.previewUrl || silhouetteUrl || generatedBrand?.logo?.url,
      cleanUrl: variations['preto']?.cleanUrl || silhouetteUrl || generatedBrand?.logo?.url,
      bgColor: undefined,
      checkerboard: true,
      filter: 'none' as const,
    },
    {
      id: 'neutro-claro',
      label: 'Neutro Claro',
      previewUrl: variations['neutro-claro']?.previewUrl || darkLogoUrl || generatedBrand?.logo?.url,
      cleanUrl: variations['neutro-claro']?.cleanUrl || darkLogoUrl || generatedBrand?.logo?.url,
      bgColor: generatedBrand?.colorPalette?.[2]?.hex || '#F8F9FA',
      checkerboard: false,
      filter: 'none' as const,
    },
    {
      id: 'tons-neutros',
      label: 'Tons Neutros',
      previewUrl: variations['tons-neutros']?.previewUrl || invertedUrl || generatedBrand?.logo?.url,
      cleanUrl: variations['tons-neutros']?.cleanUrl || invertedUrl || generatedBrand?.logo?.url,
      bgColor: generatedBrand?.colorPalette?.[3]?.hex || '#1F2937',
      checkerboard: false,
      filter: 'none' as const,
    },
  ];

  // SECURITY: Execute actual download using CLEAN URL (no watermark)
  const executeActualDownload = (cleanUrl?: string, name?: string, bgColor?: string) => {
    if (!cleanUrl) {
      toast.error("Nenhuma logo para baixar");
      return;
    }
    downloadProcessedVariation(cleanUrl, name || 'logo', 'none', bgColor);
  };

  // Handle download for specific variation - uses CLEAN URL after payment
  const handleVariationDownload = (variation: ReturnType<typeof getLogoVariations>[0]) => {
    // 1. Check if user is logged in
    if (!session || !user) {
      setPendingDownload(variation);
      setShowSignupModal(true);
      return;
    }
    
    // 2. Check if user has PAID (crucial!)
    if (!hasPurchased) {
      // Track InitiateCheckout (Meta + TikTok)
      trackInitiateCheckout({ value: 89.90 });
      setPendingDownload(variation);
      setShowPaywall(true);
      return;
    }
    
    // 3. If paid, execute download with CLEAN URL (no watermark!)
    executeActualDownload(variation.cleanUrl, variation.id, variation.bgColor);
  };

  // Handle main hero download - uses CLEAN URL
  const handleDownload = () => {
    // 1. Check if user is logged in
    if (!session || !user) {
      setPendingDownload(null); // Hero logo, not a variation
      setShowSignupModal(true);
      return;
    }
    
    // 2. Check if user has PAID
    if (!hasPurchased) {
      // Track InitiateCheckout (Meta + TikTok)
      trackInitiateCheckout({ value: 89.90 });
      setPendingDownload(null);
      setShowPaywall(true);
      return;
    }
    
    // 3. If paid, execute download using CLEAN URL
    executeActualDownload(heroCleanUrl || generatedBrand?.logo?.url, 'logo');
  };

  // Handle cross-sell: Claim free mascot for brand kit purchasers
  const handleClaimFreeMascot = async () => {
    // 1. Check if user is logged in
    if (!session?.user) {
      toast.error("Faça login para resgatar seu mascote grátis");
      setShowSignupModal(true);
      return;
    }

    // 2. Check if user has purchased brand_kit
    if (!hasPurchased) {
      toast.error("Você precisa ter comprado o Kit Marca para ganhar o mascote");
      return;
    }

    try {
      // 3. Check if user already has mascot access
      const { data: existingMascot } = await supabase
        .from('purchases')
        .select('id')
        .eq('user_id', session.user.id)
        .eq('product_type', 'mascot')
        .maybeSingle();

      if (existingMascot) {
        // Already has mascot - just redirect
        toast.success("Você já tem acesso ao mascote!");
        navigate("/mascote-generator");
        return;
      }

      // 4. Get order_id from brand_kit purchase to link
      const { data: brandKitPurchase } = await supabase
        .from('purchases')
        .select('order_id')
        .eq('user_id', session.user.id)
        .eq('product_type', 'brand_kit')
        .maybeSingle();

      // 5. Insert free mascot purchase (valid for 30 days)
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 30);

      const { error } = await supabase
        .from('purchases')
        .insert({
          user_id: session.user.id,
          product_type: 'mascot',
          order_id: brandKitPurchase?.order_id || crypto.randomUUID(),
          expires_at: expiresAt.toISOString(),
        });

      if (error) {
        console.error('Error granting free mascot:', error);
        toast.error("Erro ao resgatar mascote. Tente novamente.");
        return;
      }

      // 6. Success - redirect
      toast.success("🎁 Mascote grátis liberado! Vamos criar?");
      navigate("/mascote-generator");

    } catch (error) {
      console.error('Error in handleClaimFreeMascot:', error);
      toast.error("Erro inesperado. Tente novamente.");
    }
  };
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Block Ctrl+S, Ctrl+U, Ctrl+Shift+I
      if (e.ctrlKey && (e.key === 's' || e.key === 'S' || e.key === 'u' || e.key === 'U')) {
        e.preventDefault();
      }
      if (e.ctrlKey && e.shiftKey && (e.key === 'i' || e.key === 'I')) {
        e.preventDefault();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const copyToClipboard = (text: string, label?: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label || text} copiado!`);
  };

  // hexToCmyk moved to @/lib/utils

  // Create silhouette image - converts logo pixels to black, keeps background transparent
  const createSilhouetteImage = (
    originalUrl: string,
    onComplete: (silhouetteUrl: string) => void
  ) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      // Detect background color from top-left corner
      const bgR = data[0], bgG = data[1], bgB = data[2];
      const bgThreshold = 40; // Color tolerance
      
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i], g = data[i+1], b = data[i+2], a = data[i+3];
        
        // If pixel is similar to background OR transparent → make transparent
        const isBg = Math.abs(r - bgR) < bgThreshold && 
                     Math.abs(g - bgG) < bgThreshold && 
                     Math.abs(b - bgB) < bgThreshold;
        
        if (isBg || a < 128) {
          data[i+3] = 0; // Transparent
        } else {
          // Logo pixel → black
          data[i] = 0;
          data[i+1] = 0;
          data[i+2] = 0;
          data[i+3] = 255;
        }
      }
      
      ctx.putImageData(imageData, 0, 0);
      onComplete(canvas.toDataURL('image/png'));
    };
    img.onerror = () => {
      console.error('Failed to load image for silhouette');
      onComplete(originalUrl); // Fallback to original
    };
    img.src = originalUrl;
  };


  // Download processed variation with canvas
  const downloadProcessedVariation = async (
    originalUrl: string, 
    name: string, 
    filter: 'none' | 'invert' | 'grayscale' | 'opacity' | 'silhouette',
    bgColor?: string
  ) => {
    try {
      console.log('📥 Iniciando download:', { originalUrl, name, filter, bgColor });
      
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = originalUrl;
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });
      
      const canvas = document.createElement('canvas');
      canvas.width = 1080;
      canvas.height = 1080;
      const ctx = canvas.getContext('2d')!;
      
      // Draw background if specified
      if (bgColor) {
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      
      // Apply filters
      switch (filter) {
        case 'invert':
          ctx.filter = 'invert(1)';
          break;
        case 'grayscale':
          ctx.filter = 'grayscale(1)';
          break;
        case 'opacity':
          ctx.globalAlpha = 0.3;
          break;
        default:
          break;
      }
      
      // Calculate dimensions to COVER canvas (no padding, no transparent gaps)
      // Use cover logic: scale up to fill, crop excess
      const scale = Math.max(canvas.width / img.width, canvas.height / img.height);
      const drawWidth = img.width * scale;
      const drawHeight = img.height * scale;
      const x = (canvas.width - drawWidth) / 2;
      const y = (canvas.height - drawHeight) / 2;
      
      ctx.drawImage(img, x, y, drawWidth, drawHeight);
      
      // Download
      const link = document.createElement('a');
      const dataUrl = canvas.toDataURL('image/png');
      
      console.log('📦 Blob gerado:', { 
        length: dataUrl.length, 
        preview: dataUrl.substring(0, 80) 
      });
      
      if (dataUrl.length < 100) {
        toast.error("Arquivo gerado está vazio ou corrompido!");
        console.error('❌ Arquivo corrompido - muito pequeno');
        return;
      }
      
      link.href = dataUrl;
      link.download = `${formData.brandName}-${name}.png`;
      link.click();
      toast.success(`Download ${name} iniciado!`);
    } catch (error) {
      console.error('❌ Erro no download:', error);
      toast.error("Erro ao processar imagem. Tente novamente.");
    }
  };

  // Generated brand data (from API)
  const [generatedBrand, setGeneratedBrand] = useState<{
    success: boolean;
    logo: { url: string; transparentUrl?: string; prompt: string };
    fontKit: {
      display: { name: string; purpose: string; downloadLink?: string };
      titles: { name: string; purpose: string; downloadLink?: string };
      body: { name: string; purpose: string; downloadLink?: string };
    };
    colorPalette: Array<{ name: string; hex: string; role: string }>;
    copywriting?: {
      tagline: string;
      bio: string;
    };
  } | null>(null);

  // Dynamic font loading - loads fonts from generated brand
  useDynamicFont([
    generatedBrand?.fontKit?.display?.name,
    generatedBrand?.fontKit?.titles?.name,
    generatedBrand?.fontKit?.body?.name,
  ]);

  // Sync generatedBrand to window for auto-save access
  useEffect(() => {
    (window as any).__clicklogos_generatedBrand = generatedBrand;
  }, [generatedBrand]);

  // =============================================================
  // SAFETY FALLBACK: Redirect to step 1 if critical data missing
  // =============================================================
  useEffect(() => {
    // Only check after auth is ready and initial restoration is complete
    if (!isAuthReady || !hasRestoredRef.current) return;
    
    // Step 5 requires rawLogoUrl
    if (currentStep === 5 && !rawLogoUrl && !isGenerating) {
      console.warn('[Safety] Step 5 without rawLogoUrl, redirecting to step 1');
      setCurrentStep(1);
      toast.warning("Sessão expirada. Comece novamente.");
      return;
    }
    
    // Step 6 requires generatedBrand
    if (currentStep === 6 && !generatedBrand && !isFinalizingBrand) {
      console.warn('[Safety] Step 6 without generatedBrand, redirecting to step 1');
      setCurrentStep(1);
      toast.warning("Sessão expirada. Comece novamente.");
      return;
    }
  }, [currentStep, rawLogoUrl, generatedBrand, isAuthReady, isGenerating, isFinalizingBrand]);

  // Handler for OAuth redirect - saves state BEFORE leaving page
  const handleBeforeOAuthRedirect = () => {
    console.log('[OAuth] Saving wizard state before redirect...');
    saveWizardState(formData, currentStep, generatedBrand, generationCount, rawLogoUrl);
    markOAuthPending();
  };

  // State for processed image variations - SECURITY: Clean (original) vs Preview (watermarked)
  const [heroPreviewUrl, setHeroPreviewUrl] = useState<string | null>(null);
  const [heroCleanUrl, setHeroCleanUrl] = useState<string | null>(null);
  
  // Variations with both preview and clean URLs
  const [variations, setVariations] = useState<{
    [key: string]: { previewUrl: string; cleanUrl: string };
  }>({});

  // Legacy states (being replaced by variations object)
  const [silhouetteUrl, setSilhouetteUrl] = useState<string | null>(null);
  const [invertedUrl, setInvertedUrl] = useState<string | null>(null);
  const [transparentUrl, setTransparentUrl] = useState<string | null>(null);
  const [darkLogoUrl, setDarkLogoUrl] = useState<string | null>(null);

  // SECURITY: Burn watermark permanently into image via Canvas
  const burnWatermark = (
    imageUrl: string,
    onComplete: (previewUrl: string, cleanUrl: string) => void
  ) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      
      // 1. Draw original image
      ctx.drawImage(img, 0, 0);
      
      // Save CLEAN version BEFORE adding watermark
      const cleanDataUrl = canvas.toDataURL('image/png');
      
      // 2. Configure watermark text
      ctx.save();
      ctx.globalAlpha = 0.30;
      ctx.font = '600 13px system-ui, sans-serif';
      ctx.fillStyle = '#000000';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      // 3. Rotation angle (-25 degrees)
      const angle = -25 * Math.PI / 180;
      
      // 4. Calculate grid to fill ENTIRE area (including after rotation)
      const text = 'ClickLogos.com.br';
      const textWidth = 150;
      const textHeight = 60;
      
      // Expand area to cover rotation - use diagonal
      const diagonal = Math.sqrt(canvas.width ** 2 + canvas.height ** 2);
      const startX = -diagonal;
      const startY = -diagonal;
      const endX = diagonal * 2;
      const endY = diagonal * 2;
      
      // Translate to center, rotate, then draw grid
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(angle);
      
      for (let y = startY; y < endY; y += textHeight) {
        for (let x = startX; x < endX; x += textWidth) {
          ctx.fillText(text, x, y);
        }
      }
      
      ctx.restore();
      
      // 5. Preview = image with BURNED watermark
      const previewDataUrl = canvas.toDataURL('image/png');
      
      onComplete(previewDataUrl, cleanDataUrl);
    };
    img.onerror = () => {
      console.error('Failed to load image for watermark burn-in');
      onComplete(imageUrl, imageUrl); // Fallback to original
    };
    img.src = imageUrl;
  };

  // Create transparent image - removes background, keeps logo colors
  const createTransparentImage = (
    originalUrl: string,
    onComplete: (transparentUrl: string) => void
  ) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      // Detect background color from top-left corner
      const bgR = data[0], bgG = data[1], bgB = data[2];
      const bgThreshold = 45; // Tolerance
      
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i], g = data[i+1], b = data[i+2];
        
        // If pixel is similar to background → transparent
        const isBg = Math.abs(r - bgR) < bgThreshold && 
                     Math.abs(g - bgG) < bgThreshold && 
                     Math.abs(b - bgB) < bgThreshold;
        
        if (isBg) {
          data[i+3] = 0; // Alpha = 0 (transparent)
        }
        // Otherwise, keep original colors
      }
      
      ctx.putImageData(imageData, 0, 0);
      onComplete(canvas.toDataURL('image/png'));
    };
    img.onerror = () => onComplete(originalUrl);
    img.src = originalUrl;
  };

  // Create dark logo image - paints logo in neutral dark color
  const createDarkLogoImage = (
    originalUrl: string,
    darkColor: string,
    onComplete: (darkLogoUrl: string) => void
  ) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      // Parse dark color
      const dr = parseInt(darkColor.slice(1, 3), 16);
      const dg = parseInt(darkColor.slice(3, 5), 16);
      const db = parseInt(darkColor.slice(5, 7), 16);
      
      // Detect background color from top-left corner
      const bgR = data[0], bgG = data[1], bgB = data[2];
      const bgThreshold = 45;
      
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i], g = data[i+1], b = data[i+2];
        
        const isBg = Math.abs(r - bgR) < bgThreshold && 
                     Math.abs(g - bgG) < bgThreshold && 
                     Math.abs(b - bgB) < bgThreshold;
        
        if (isBg) {
          data[i+3] = 0; // Transparent
        } else {
          // Paint logo in dark color
          data[i] = dr;
          data[i+1] = dg;
          data[i+2] = db;
          data[i+3] = 255;
        }
      }
      
      ctx.putImageData(imageData, 0, 0);
      onComplete(canvas.toDataURL('image/png'));
    };
    img.onerror = () => onComplete(originalUrl);
    img.src = originalUrl;
  };

  // Create inverted image - converts logo pixels to white, keeps background transparent
  const createInvertedImage = (
    originalUrl: string,
    onComplete: (invertedUrl: string) => void
  ) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      // Detect background color from top-left corner
      const bgR = data[0], bgG = data[1], bgB = data[2];
      const bgThreshold = 40;
      
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i], g = data[i+1], b = data[i+2], a = data[i+3];
        
        const isBg = Math.abs(r - bgR) < bgThreshold && 
                     Math.abs(g - bgG) < bgThreshold && 
                     Math.abs(b - bgB) < bgThreshold;
        
        if (isBg || a < 128) {
          data[i+3] = 0; // Transparent
        } else {
          // Logo pixel → white (inverted)
          data[i] = 255;
          data[i+1] = 255;
          data[i+2] = 255;
          data[i+3] = 255;
        }
      }
      
      ctx.putImageData(imageData, 0, 0);
      onComplete(canvas.toDataURL('image/png'));
    };
    img.onerror = () => onComplete(originalUrl);
    img.src = originalUrl;
  };

  // Helper: Create silhouette from ALREADY TRANSPARENT image
  const createSilhouetteFromTransparent = (
    transparentUrl: string,
    onComplete: (silhouetteUrl: string) => void
  ) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      for (let i = 0; i < data.length; i += 4) {
        // Se pixel tem alpha > 50, pintar de preto puro
        if (data[i+3] > 50) {
          data[i] = 0;     // R
          data[i+1] = 0;   // G
          data[i+2] = 0;   // B
          data[i+3] = 255; // Alpha total
        }
      }
      
      ctx.putImageData(imageData, 0, 0);
      onComplete(canvas.toDataURL('image/png'));
    };
    img.onerror = () => onComplete(transparentUrl);
    img.src = transparentUrl;
  };

  // Helper: Create dark-colored logo from ALREADY TRANSPARENT image
  const createDarkLogoFromTransparent = (
    transparentUrl: string,
    darkColor: string,
    onComplete: (darkLogoUrl: string) => void
  ) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      const dr = parseInt(darkColor.slice(1, 3), 16);
      const dg = parseInt(darkColor.slice(3, 5), 16);
      const db = parseInt(darkColor.slice(5, 7), 16);
      
      for (let i = 0; i < data.length; i += 4) {
        if (data[i+3] > 50) {
          data[i] = dr;
          data[i+1] = dg;
          data[i+2] = db;
          data[i+3] = 255;
        }
      }
      
      ctx.putImageData(imageData, 0, 0);
      onComplete(canvas.toDataURL('image/png'));
    };
    img.onerror = () => onComplete(transparentUrl);
    img.src = transparentUrl;
  };

  // Helper: Create inverted (white) logo from ALREADY TRANSPARENT image
  const createInvertedFromTransparent = (
    transparentUrl: string,
    onComplete: (invertedUrl: string) => void
  ) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      for (let i = 0; i < data.length; i += 4) {
        if (data[i+3] > 50) {
          data[i] = 255;
          data[i+1] = 255;
          data[i+2] = 255;
          data[i+3] = 255;
        }
      }
      
      ctx.putImageData(imageData, 0, 0);
      onComplete(canvas.toDataURL('image/png'));
    };
    img.onerror = () => onComplete(transparentUrl);
    img.src = transparentUrl;
  };

  // SECURITY: Process all image variations with burn-in watermark
  // Uses transparentUrl from API if available, otherwise falls back to Canvas
  useEffect(() => {
    if (generatedBrand?.logo?.url) {
      // 1. HERO: Burn watermark on original image (with background for visual impact)
      burnWatermark(generatedBrand.logo.url, (preview, clean) => {
        setHeroPreviewUrl(preview);
        setHeroCleanUrl(clean);
      });
      
      // 2. Determine base for variations: API transparent or Canvas fallback
      const processVariationsWithBase = (baseTransparentUrl: string) => {
        console.log('🎨 Processing variations with base:', baseTransparentUrl.substring(0, 80));
        
        // COLORIDA - usa transparent diretamente
        setTransparentUrl(baseTransparentUrl); // Legacy
        burnWatermark(baseTransparentUrl, (preview, clean) => {
          setVariations(prev => ({
            ...prev,
            colorida: { previewUrl: preview, cleanUrl: clean }
          }));
        });
        
        // PRETO 100% - silhouette from transparent
        createSilhouetteFromTransparent(baseTransparentUrl, (silhouetteClean) => {
          setSilhouetteUrl(silhouetteClean); // Legacy
          burnWatermark(silhouetteClean, (preview, clean) => {
            setVariations(prev => ({
              ...prev,
              preto: { previewUrl: preview, cleanUrl: clean }
            }));
          });
        });
        
        // TONS NEUTROS (inverted) from transparent
        createInvertedFromTransparent(baseTransparentUrl, (invertedClean) => {
          setInvertedUrl(invertedClean); // Legacy
          burnWatermark(invertedClean, (preview, clean) => {
            setVariations(prev => ({
              ...prev,
              'tons-neutros': { previewUrl: preview, cleanUrl: clean }
            }));
          });
        });
        
        // NEUTRO CLARO (dark logo) from transparent
        const darkColor = generatedBrand?.colorPalette?.[3]?.hex || '#1F2937';
        createDarkLogoFromTransparent(baseTransparentUrl, darkColor, (darkLogoClean) => {
          setDarkLogoUrl(darkLogoClean); // Legacy
          burnWatermark(darkLogoClean, (preview, clean) => {
            setVariations(prev => ({
              ...prev,
              'neutro-claro': { previewUrl: preview, cleanUrl: clean }
            }));
          });
        });
      };
      
      // Check if API returned transparentUrl
      if (generatedBrand.logo.transparentUrl) {
        console.log('✅ Using professional API transparent image (InSPyReNet)');
        processVariationsWithBase(generatedBrand.logo.transparentUrl);
      } else {
        console.warn('⚠️ Fallback: using Canvas background removal');
        // Fallback: usar método Canvas antigo
        createTransparentImage(generatedBrand.logo.url, (fallbackTransparent) => {
          processVariationsWithBase(fallbackTransparent);
        });
      }
    }
  }, [generatedBrand?.logo?.url, generatedBrand?.logo?.transparentUrl, generatedBrand?.colorPalette]);

  // 🆕 SYNC: Upload variations to Storage and update brand_kits when ready
  useEffect(() => {
    // Only run if we have a saved kit ID and variations are ready
    if (!savedBrandKitId || variationsUploaded) return;
    if (!user) return;
    
    // Check if all variations are ready (have clean URLs in variations object)
    const variationsReady = 
      variations['colorida']?.cleanUrl &&
      variations['preto']?.cleanUrl &&
      variations['neutro-claro']?.cleanUrl &&
      variations['tons-neutros']?.cleanUrl;
    
    if (!variationsReady) return;
    
    const uploadAndSave = async () => {
      console.log('📤 Uploading variations to Storage...');
      
      try {
        // Upload all variations
        const uploadedUrls = await uploadLogoVariations(user.id, savedBrandKitId, {
          colorida: variations['colorida'].cleanUrl,
          preto: variations['preto'].cleanUrl,
          neutroClaro: variations['neutro-claro'].cleanUrl,
          tonsNeutros: variations['tons-neutros'].cleanUrl,
        });
        
        console.log('✅ Variations uploaded:', uploadedUrls);
        
        // Update brand_kits with uploaded URLs
        const { error: updateError } = await supabase
          .from('brand_kits')
          .update({
            logo_color_transparent_url: uploadedUrls.logo_color_transparent_url,
            logo_black_transparent_url: uploadedUrls.logo_black_transparent_url,
            logo_neutral_light_transparent_url: uploadedUrls.logo_neutral_light_transparent_url,
            logo_neutral_tones_url: uploadedUrls.logo_neutral_tones_url,
          })
          .eq('id', savedBrandKitId);
        
        if (updateError) {
          console.error('❌ Error updating brand_kit with variations:', updateError);
        } else {
          console.log('✅ Brand kit updated with variation URLs');
          setVariationsUploaded(true);
        }
      } catch (error) {
        console.error('❌ Error uploading variations:', error);
      }
    };
    
    uploadAndSave();
  }, [savedBrandKitId, variations, variationsUploaded, user]);

  // 🆕 SNAPSHOT: Save complete snapshot for ApprovedBrand page after variations are ready
  useEffect(() => {
    // Only run when on Step 6 (results) and we have a saved kit ID and variations are uploaded
    if (currentStep !== 6 || !savedBrandKitId || !variationsUploaded) return;
    if (!generatedBrand) return;
    
    const saveSnapshot = async () => {
      console.log('📸 Saving approved snapshot...');
      
      // Build the complete snapshot with all data visible on Step 6
      const snapshot = {
        brandName: formData.brandName,
        heroUrl: heroPreviewUrl || generatedBrand.logo?.url || '',
        heroCleanUrl: heroCleanUrl || generatedBrand.logo?.transparentUrl || generatedBrand.logo?.url || '',
        colorPalette: generatedBrand.colorPalette?.map((color, index) => ({
          name: color.name || ['Primária', 'Secundária', 'Neutra Clara', 'Neutra Escura'][index] || `Cor ${index + 1}`,
          hex: color.hex,
          role: ['primary', 'secondary', 'neutral-light', 'neutral-dark'][index] || undefined,
        })) || [],
        fonts: {
          display: generatedBrand.fontKit?.display ? {
            name: generatedBrand.fontKit.display.name,
            downloadLink: generatedBrand.fontKit.display.downloadLink,
          } : undefined,
          titles: generatedBrand.fontKit?.titles ? {
            name: generatedBrand.fontKit.titles.name,
            downloadLink: generatedBrand.fontKit.titles.downloadLink,
          } : undefined,
          body: generatedBrand.fontKit?.body ? {
            name: generatedBrand.fontKit.body.name,
            downloadLink: generatedBrand.fontKit.body.downloadLink,
          } : undefined,
        },
        copywriting: {
          tagline: generatedBrand.copywriting?.tagline || undefined,
          bio: generatedBrand.copywriting?.bio || undefined,
        },
        variations: [
          {
            id: 'colorida',
            label: 'Colorida',
            previewUrl: variations['colorida']?.previewUrl || transparentUrl || generatedBrand.logo?.url || '',
            cleanUrl: variations['colorida']?.cleanUrl || transparentUrl || generatedBrand.logo?.url || '',
            checkerboard: true,
          },
          {
            id: 'preto',
            label: 'Preto 100%',
            previewUrl: variations['preto']?.previewUrl || silhouetteUrl || generatedBrand.logo?.url || '',
            cleanUrl: variations['preto']?.cleanUrl || silhouetteUrl || generatedBrand.logo?.url || '',
            checkerboard: true,
          },
          {
            id: 'neutro-claro',
            label: 'Neutro Claro',
            previewUrl: variations['neutro-claro']?.previewUrl || darkLogoUrl || generatedBrand.logo?.url || '',
            cleanUrl: variations['neutro-claro']?.cleanUrl || darkLogoUrl || generatedBrand.logo?.url || '',
            bgColor: generatedBrand.colorPalette?.[2]?.hex || '#F8F9FA',
            checkerboard: false,
          },
          {
            id: 'tons-neutros',
            label: 'Tons Neutros',
            previewUrl: variations['tons-neutros']?.previewUrl || invertedUrl || generatedBrand.logo?.url || '',
            cleanUrl: variations['tons-neutros']?.cleanUrl || invertedUrl || generatedBrand.logo?.url || '',
            bgColor: generatedBrand.colorPalette?.[3]?.hex || '#1F2937',
            checkerboard: false,
          },
        ],
        savedAt: new Date().toISOString(),
      };
      
      console.log('📸 Snapshot data:', snapshot);
      
      const { error } = await supabase
        .from('brand_kits')
        .update({ approved_snapshot: snapshot })
        .eq('id', savedBrandKitId);
      
      if (error) {
        console.error('❌ Error saving snapshot:', error);
      } else {
        console.log('✅ Approved snapshot saved successfully');
      }
    };
    
    saveSnapshot();
  }, [currentStep, savedBrandKitId, variationsUploaded, generatedBrand, formData.brandName, heroPreviewUrl, heroCleanUrl, variations, transparentUrl, silhouetteUrl, darkLogoUrl, invertedUrl]);

  // AI suggestion logic moved to Step4Concept component
  // Legacy content data
  const features = [{
    icon: Palette,
    title: "Mascote Único",
    description: "Crie um personagem exclusivo que representa sua marca"
  }, {
    icon: Video,
    title: "Vídeos Animados",
    description: "Gere vídeos curtos com narração para redes sociais"
  }, {
    icon: Package,
    title: "Kit Marca Profissional",
    description: "Logo, cores, fontes e mensagens prontas para usar"
  }];

  const howItWorksSteps = [{
    icon: Keyboard,
    title: "1) Digite o nome da empresa",
    description: "Comece pelo básico. Nada de cadastros longos, só o nome e pronto."
  }, {
    icon: Lightbulb,
    title: "2) Defina seu estilo",
    description: "Selecione o contexto do seu negócio ou personalize o visual do seu jeito."
  }, {
    icon: Droplets,
    title: "3) Selecione a paleta de cores",
    description: "Selecione as cores que vão destacar sua marca e atrair clientes."
  }, {
    icon: Type,
    title: "4) Selecione a tipografia",
    description: "Selecione a fonte principal que dará a voz e o tom da sua marca."
  }, {
    icon: RefreshCw,
    title: "5) Refine até amar",
    description: "Não gostou de primeira? Você pode recriar e ajustar a mesma marca até 10x."
  }, {
    icon: CheckCircle,
    title: "6) Pronto!",
    description: "Tudo pronto para comunicar melhor o seu negócio e vender mais."
  }];

  const whatYouGetItems = [{
    icon: Sparkles,
    title: "Logo Profissional Completa",
    description: "Símbolo único + nome da sua empresa, com design equilibrado e pronto para uso."
  }, {
    icon: Droplets,
    title: "Paleta de Cores Oficial",
    description: "Receba os códigos exatos (HEX) para manter o padrão em impressos e na internet."
  }, {
    icon: Type,
    title: "Fontes Prontas para Baixar",
    description: "Link direto para baixar e instalar a tipografia oficial da sua marca gratuitamente."
  }, {
    icon: Layers,
    title: "PNG em Alta Definição",
    description: "Arquivo de qualidade máxima, ideal para fachadas, banners e grandes impressões."
  }, {
    icon: FileImage,
    title: "Logo com Fundo Transparente",
    description: "Variações prontas para aplicar sobre fotos, Instagram, WhatsApp e cardápios digitais."
  }, {
    icon: FileText,
    title: "Kit Resumo da Marca",
    description: "Sua identidade visual completa condensada em uma única página: logo, cores, fontes e variações."
  }];

  const trustPoints = [{
    icon: MessageCircle,
    title: "Suporte Humano no WhatsApp",
    description: "Dúvidas? Fale com gente de verdade. Nossa equipe analisa e responde suas solicitações pessoalmente."
  }, {
    icon: LockOpen,
    title: "Pagou, Baixou. Sem Assinatura.",
    description: "Livre-se das mensalidades. Aqui você paga uma única vez e os arquivos são seus para sempre."
  }, {
    icon: Award,
    title: "Criado por Especialistas do Brasil",
    description: "Ferramenta desenvolvida por Designer de Marcas com mais de 10 anos de mercado."
  }];

  const plans = [{
    name: "Mascote Oficial",
    price: "R$ 29,90",
    originalPrice: "R$ 89,90",
    discount: "67% OFF",
    period: "único",
    available: true,
    features: ["3 variações de mascote geradas por IA", "Arquivos em Alta Resolução (PNG)", "Versão com fundo transparente", "Uso comercial liberado"],
    buttonText: "Criar Mascote Agora"
  }, {
    name: "Kit Marca Completo",
    price: "R$ 59,90",
    originalPrice: "R$ 299,90",
    discount: "80% OFF",
    period: "único",
    popular: true,
    available: true,
    features: ["Logo Profissional (Símbolo + Texto)", "Arquivos PNG (Fundo transparente e alta def.)", "Paleta de Cores Oficial (Códigos Hex)", "Tipografia (Nome das fontes para download)", "Uso comercial liberado"],
    bonus: "Bônus: Inclui 1 Mascote grátis!",
    buttonText: "Começar Agora"
  }, {
    name: "Vídeos Animados",
    price: "Em breve",
    period: "aguarde",
    available: false,
    features: ["Mascote + Animação", "Voz Sintética em PT-BR", "Roteiros automáticos", "Música licenciada"],
    buttonText: "Quero ser avisado"
  }];

  return (
    <main className="min-h-screen">
      <PromoBanner />
      
      {/* Wizard Section - THE APP */}
      <section className="relative bg-gradient-hero min-h-screen py-6 sm:py-8 md:py-12 px-6 md:px-10 lg:px-6">
        <div className="container mx-auto relative z-10">
          {/* Header with Logo and Auth */}
          <div className="flex items-center justify-between mb-4 sm:mb-6 h-14 sm:h-16">
            <div 
              className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={handleResetWizard}
              role="button"
              aria-label="Voltar ao início"
            >
              <img 
                src={marcaClickLogos}
                alt="ClickLogos" 
                className="h-8 sm:h-10 w-auto"
                fetchPriority="high"
              />
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3">
              {currentStep > 1 && (
                <span className="text-white/80 text-xs sm:text-sm font-medium hidden sm:inline">
                  Passo {currentStep} de {totalSteps}
                </span>
              )}
              
              {/* Auth State - Show skeleton while checking */}
              {isCheckingAuth ? (
                <Skeleton className="w-8 h-8 rounded-full bg-white/20" />
              ) : session ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-white hover:bg-white/10 gap-2">
                      <Avatar className="w-8 h-8">
                        <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                          {user?.email?.charAt(0).toUpperCase() || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-background">
                    <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                      <Folder className="w-4 h-4 mr-2" />
                      Meus Projetos
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                      <LogOut className="w-4 h-4 mr-2" />
                      Sair
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowSignupModal(true)}
                  className="text-white hover:bg-white/10"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Entrar
                </Button>
              )}
            </div>
          </div>

          {/* Progress Bar - Reserve space to prevent layout shift */}
          <div className="w-full h-1.5 mb-8">
            {currentStep > 1 && (
              <div className="w-full bg-white/20 rounded-full h-full">
                <div
                  className="h-full bg-white rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                />
              </div>
            )}
          </div>

          {/* Step 1: Data Entry - THE HOME */}
          {currentStep === 1 && (
            <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 lg:gap-20 items-center">
              {/* Left Column: Text */}
              <div className="text-center lg:text-left order-1 lg:order-1 flex flex-col">
                {/* TÍTULO IMPACTANTE - VEED Style: Bold, Clean, Premium */}
                <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-7xl font-display font-bold text-white mb-4 sm:mb-6 md:mb-8 leading-[1.1] tracking-tight text-balance">
                  {/* 3 linhas fixas em TODAS as telas */}
                  <span className="flex flex-col">
                    <span className="animate-fade-up">Sua marca</span>
                    <span className="animate-fade-up [animation-delay:100ms]">pronta antes</span>
                    <span className="animate-fade-up [animation-delay:200ms]">do café esfriar.</span>
                  </span>
                </h1>
                {/* Subtitle - Compact on mobile, expanded on desktop */}
                <p className="text-white/80 text-xs sm:text-base lg:text-xl mb-4 md:mb-8 lg:mb-10 leading-relaxed font-normal animate-fade-up delay-200">
                  <span className="sm:hidden">Identidade visual completa. Sem agências caras.</span>
                  <span className="hidden sm:inline">
                    Sem agências caras, sem reuniões intermináveis.
                    <br />
                    Identidade visual completa com a qualidade que seu negócio precisa.
                  </span>
                </p>
                
                {/* BULLET POINTS - VEED Style: More spacing */}
                <div className="flex flex-col gap-3 md:gap-4 lg:gap-5 mb-6 lg:mb-0 order-3 lg:order-none mt-3 lg:mt-0 animate-fade-up delay-500">
                  <div className="flex items-center gap-3 lg:gap-4 text-white/90">
                    <div className="w-6 h-6 lg:w-7 lg:h-7 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-white" />
                    </div>
                    <span className="text-sm sm:text-base lg:text-lg">Sua marca em 1 minuto</span>
                  </div>
                  <div className="flex items-center gap-3 lg:gap-4 text-white/90">
                    <div className="w-6 h-6 lg:w-7 lg:h-7 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-white" />
                    </div>
                    <span className="text-sm sm:text-base lg:text-lg">Paleta de cores + tipografia</span>
                  </div>
                  <div className="flex items-center gap-3 lg:gap-4 text-white/90">
                    <div className="w-6 h-6 lg:w-7 lg:h-7 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-white" />
                    </div>
                    <span className="text-sm sm:text-base lg:text-lg">Mascote exclusivo para sua marca</span>
                  </div>
                </div>

                {/* CTA MOBILE - Reordenado para ANTES dos bullets (order-2) */}
                <div className="lg:hidden order-2 lg:order-none mb-4 animate-fade-up delay-300">
                  <Button 
                    size="lg" 
                    className="w-full h-14 text-lg font-semibold bg-white text-primary hover:bg-white/95 shadow-[0_12px_32px_rgba(0,0,0,0.08)] hover:scale-[1.02] transition-all duration-200"
                    onClick={() => document.querySelector('#brand-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  >
                    Começar Agora — É Grátis
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                  <p className="text-center text-white/60 text-xs mt-2">Sua marca pronta em 1 minuto</p>
                </div>
              </div>

              {/* Right Column: Form Card - VEED Style: Premium shadows, more padding */}
              <div id="brand-form" className="order-2 lg:order-2 animate-fade-up delay-300 scroll-mt-8">
                <Card className="p-7 sm:p-8 md:p-10 lg:p-12 space-y-6 md:space-y-8 bg-white shadow-[0_24px_64px_rgba(0,0,0,0.06)] border-0 rounded-2xl lg:rounded-3xl">
                  {/* 1. Nome da Marca */}
                  <div className="space-y-2.5 md:space-y-3">
                    <label className="text-sm font-medium text-foreground/80 flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-primary" />
                      Nome da Marca
                    </label>
                    <Input
                      placeholder="Ex: Doce Sabor"
                      value={formData.brandName}
                      onChange={(e) => updateFormData("brandName", e.target.value)}
                      className="text-xl md:text-2xl h-14 md:h-16 font-semibold border border-border/60 focus:border-primary"
                      autoFocus
                    />
                  </div>

                  {/* 2. Segmento */}
                  <div className="space-y-2.5 md:space-y-3">
                    <label className="text-sm font-medium text-foreground/80 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-muted-foreground" />
                      Segmento do Negócio
                    </label>
                    <Select value={formData.segment} onValueChange={(value) => updateFormData("segment", value)}>
                      <SelectTrigger className="h-12 border-border/60">
                        <SelectValue placeholder="Selecione o segmento" />
                      </SelectTrigger>
                      <SelectContent>
                        {SEGMENTS.map((segment) => (
                          <SelectItem key={segment} value={segment}>
                            {segment}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* 4. Descrição */}
                  <div className="space-y-2.5 md:space-y-3">
                    <label className="text-sm font-medium text-foreground/80 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-muted-foreground" />
                      Descrição do Negócio
                    </label>
                    <Textarea
                      placeholder="Ex: Faço bolos caseiros com receitas da avó para festas e eventos..."
                      value={formData.description}
                      onChange={(e) => updateFormData("description", e.target.value)}
                      className="min-h-[100px] resize-none border-border/60"
                    />
                  </div>

                  <Button 
                    size="lg" 
                    className="w-full h-14 text-lg animate-pulse-subtle hover:animate-none shadow-[0_12px_32px_rgba(8,112,140,0.15)] hover:shadow-[0_16px_40px_rgba(8,112,140,0.2)] transition-all"
                    onClick={handleNext}
                    disabled={!canProceed()}
                  >
                    Criar Minha Marca
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Card>
              </div>
            </div>
          )}

          {/* Step 2: Personality - Lazy Loaded */}
          {currentStep === 2 && (
            <Suspense fallback={<div className="min-h-[400px] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-white" /></div>}>
              <Step2Personality
                personality={formData.personality}
                onSelect={(id) => updateFormData("personality", id)}
                onNext={handleNext}
                onBack={handleBack}
                canProceed={canProceed()}
              />
            </Suspense>
          )}

          {/* Step 3: Colors - Lazy Loaded */}
          {currentStep === 3 && (
            <Suspense fallback={<div className="min-h-[400px] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-white" /></div>}>
              <Step3Colors
                color={formData.color}
                onSelect={(id) => updateFormData("color", id)}
                onNext={handleNext}
                onBack={handleBack}
                canProceed={canProceed()}
              />
            </Suspense>
          )}

          {/* Step 4: Symbol Concept - Lazy Loaded */}
          {currentStep === 4 && (
            <Suspense fallback={<div className="min-h-[400px] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-white" /></div>}>
              <Step4Concept
                symbolConcept={formData.symbolConcept}
                conceptRefinement={formData.conceptRefinement}
                brandName={formData.brandName}
                segment={formData.segment}
                description={formData.description}
                onSelectConcept={(id) => updateFormData("symbolConcept", id)}
                onUpdateRefinement={(value) => updateFormData("conceptRefinement", value)}
                onNext={handleNext}
                onBack={handleBack}
                canProceed={canProceed()}
                isGenerating={isGenerating}
              />
            </Suspense>
          )}

          {/* Step 5: Triple Logo Selection - Lazy Loaded */}
          {currentStep === 5 && (
            <Suspense fallback={<div className="min-h-[400px] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-white" /></div>}>
              <Step5Approval
                options={logoOptions}
                selectedId={selectedLogoId}
                onSelect={(id) => {
                  setSelectedLogoId(id);
                  const selected = logoOptions.find(o => o.id === id);
                  if (selected) {
                    setRawLogoUrl(selected.rawImageUrl);
                  }
                }}
                isLoading={isLoadingOptions}
                isProcessing={isFinalizingBrand}
                brandName={formData.brandName}
                onApprove={() => handleApprovalAction('approve')}
                onBack={() => setCurrentStep(4)}
                onRecreate={() => handleApprovalAction('regenerate')}
                recreationsRemaining={recreationState.remaining}
                maxRecreations={recreationState.limit}
                isRecreating={isRecreating}
              />
            </Suspense>
          )}

          {/* Step 6: Results (Final Dashboard) */}
          {currentStep === 6 && (
            <div className="w-full max-w-5xl mx-auto animate-fade-in px-4 sm:px-6">
              {/* Celebration Header - Compact */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-1.5 rounded-full mb-3">
                  <span className="text-xl">🎉</span>
                  <span className="text-xs font-semibold text-white/90 uppercase tracking-wider">Marca Criada</span>
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-semibold text-white mb-1.5 text-balance leading-tight tracking-tight break-words">
                  Sua marca está pronta!
                </h2>
                <p className="text-sm text-white/70 break-words max-w-full">
                  Baixe a identidade visual de <span className="font-semibold break-all">{formData.brandName}</span>
                </p>
              </div>

              {/* BENTO GRID - 2x2 Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5">
                
                {/* Card 1: Logo Hero + Download Integrated */}
                <Card 
                  className="bg-gradient-to-br from-white via-slate-50 to-slate-100 p-0 overflow-hidden relative select-none shadow-[0_20px_50px_rgba(0,0,0,0.04)] ring-1 ring-black/[0.03] rounded-3xl"
                  onContextMenu={(e) => e.preventDefault()}
                >
                  {/* Logo Image */}
                  <div className="aspect-square w-full flex items-center justify-center">
                    {(heroPreviewUrl || generatedBrand?.logo?.url) ? (
                      <img
                        src={heroPreviewUrl || generatedBrand?.logo?.url}
                        alt={formData.brandName}
                        className="w-full h-full object-contain p-8 select-none pointer-events-none"
                        draggable={false}
                        onContextMenu={(e) => e.preventDefault()}
                        style={{ 
                          userSelect: 'none',
                          WebkitUserSelect: 'none',
                          WebkitUserDrag: 'none'
                        } as React.CSSProperties}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-muted">
                        <Sparkles className="w-16 h-16 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  
                  {/* Download Button - Integrated at bottom */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 via-black/40 to-transparent">
                    <Button 
                      size="lg" 
                      className="w-full h-12 text-base gap-2 bg-white text-foreground hover:bg-white/90 shadow-[0_8px_24px_rgba(0,0,0,0.08)]"
                      onClick={handleDownload}
                    >
                      <Download className="w-5 h-5 flex-shrink-0" />
                      <span className="truncate">Baixar Logo (PNG)</span>
                    </Button>
                  </div>
                </Card>

                {/* Card 2: Typography */}
                <Card className="p-5 lg:p-6 bg-gradient-to-br from-white to-slate-50/80 shadow-[0_16px_48px_rgba(0,0,0,0.04)] border-0 ring-1 ring-black/[0.03] rounded-3xl flex flex-col">
                  {/* Typography Section */}
                  <div className="flex-1 flex flex-col">
                    <h3 className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-3">
                      Tipografia
                    </h3>
                    
                    {/* Aplicação Prática - Type Specimen */}
                    <div className="bg-slate-50/70 rounded-2xl p-4 lg:p-5 mb-4 ring-1 ring-black/5 flex-1">
                      <p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-4">Aplicação Prática</p>
                      
                      {/* Nome da Marca - Fonte Display (GIGANTE) */}
                      <div className="mb-3">
                        <h2 
                          className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground break-words [text-wrap:balance] leading-tight"
                          style={{ fontFamily: `'${generatedBrand?.fontKit?.display?.name || 'Satoshi'}', sans-serif` }}
                        >
                          {formData.brandName}
                        </h2>
                        <a 
                          href={getFontDownloadLink(generatedBrand?.fontKit?.display?.name || "Satoshi")} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[9px] text-muted-foreground hover:text-primary transition-colors mt-1"
                        >
                          <ExternalLink className="w-2.5 h-2.5" />
                          Fonte: {generatedBrand?.fontKit?.display?.name || 'Satoshi'}
                        </a>
                      </div>
                      
                      {/* Título exemplo - Fonte Títulos (Médio) */}
                      <h3 
                        className="text-base lg:text-lg font-semibold text-foreground mb-2 break-words [text-wrap:balance]"
                        style={{ fontFamily: `'${generatedBrand?.fontKit?.titles?.name || 'Switzer'}', sans-serif` }}
                      >
                        "{generatedBrand?.copywriting?.tagline || `A excelência da ${formData.brandName}`}"
                      </h3>
                      
                      {/* Bio exemplo - Fonte Corpo (Pequeno) */}
                      <p 
                        className="text-sm text-muted-foreground leading-relaxed break-words line-clamp-3"
                        style={{ fontFamily: `'${generatedBrand?.fontKit?.body?.name || 'General Sans'}', sans-serif` }}
                      >
                        {generatedBrand?.copywriting?.bio || `Soluções profissionais em ${formData.segment}. Entre em contato!`}
                      </p>
                    </div>
                    
                    {/* Font Cards - Mini Row (apenas Títulos e Corpo) */}
                    <div className="grid grid-cols-2 gap-3">
                      {/* Heading */}
                      <div className="bg-slate-50 rounded-xl p-3 text-center ring-1 ring-black/5 hover:shadow-md transition-all duration-300">
                        <p className="text-[8px] text-muted-foreground uppercase tracking-wider mb-1">Títulos</p>
                        <p 
                          className="text-xl font-semibold text-foreground"
                          style={{ fontFamily: `'${generatedBrand?.fontKit?.titles?.name || 'Switzer'}', sans-serif` }}
                        >
                          Aa
                        </p>
                        <a 
                          href={getFontDownloadLink(generatedBrand?.fontKit?.titles?.name || "Switzer")} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-0.5 text-[9px] text-primary hover:underline font-medium mt-1.5"
                        >
                          <ExternalLink className="w-2.5 h-2.5" />
                          Baixar
                        </a>
                      </div>
                      
                      {/* Body */}
                      <div className="bg-slate-50 rounded-xl p-3 text-center ring-1 ring-black/5 hover:shadow-md transition-all duration-300">
                        <p className="text-[8px] text-muted-foreground uppercase tracking-wider mb-1">Corpo</p>
                        <p 
                          className="text-xl text-foreground"
                          style={{ fontFamily: `'${generatedBrand?.fontKit?.body?.name || 'General Sans'}', sans-serif` }}
                        >
                          Aa
                        </p>
                        <a 
                          href={getFontDownloadLink(generatedBrand?.fontKit?.body?.name || "General Sans")} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-0.5 text-[9px] text-primary hover:underline font-medium mt-1.5"
                        >
                          <ExternalLink className="w-2.5 h-2.5" />
                          Baixar
                        </a>
                      </div>
                    </div>
                  </div>
                  
                  {/* Generation counter badge - only for paid users */}
                  {hasPurchased && (
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-full text-xs text-primary mt-4 self-start">
                      <Sparkles className="w-3 h-3" />
                      <span>Geração {generationCount}/{MAX_GENERATIONS}</span>
                    </div>
                  )}
                </Card>

                {/* Card 3: Color Palette - Prominent Swatches */}
                <Card className="p-5 lg:p-6 bg-gradient-to-br from-white to-slate-50/80 shadow-[0_16px_48px_rgba(0,0,0,0.04)] border-0 ring-1 ring-black/[0.03] rounded-3xl flex flex-col min-h-[320px] lg:min-h-[380px]">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                      Paleta de Cores
                    </h3>
                    <span className="text-[9px] text-muted-foreground">Clique para copiar</span>
                  </div>
                  <div className="flex-1 flex items-center justify-center">
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 lg:gap-6 place-items-center">
                      {generatedBrand?.colorPalette?.map((color) => (
                        <ColorSwatch
                          key={color.name}
                          hex={color.hex}
                          name={color.name}
                          showCmyk={false}
                          size="responsive"
                        />
                      ))}
                    </div>
                  </div>
                </Card>

                {/* Card 4: Variations Preview - Compact Grid */}
                <Card className="p-5 lg:p-6 bg-gradient-to-br from-white to-slate-50/80 shadow-[0_16px_48px_rgba(0,0,0,0.04)] border-0 ring-1 ring-black/[0.03] rounded-3xl">
                  <h3 className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5 mb-4">
                    <Layers className="w-3 h-3" />
                    Variações (1080×1080px)
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {getLogoVariations().map((variation) => (
                      <div 
                        key={variation.id}
                        className={cn(
                          "rounded-xl overflow-hidden ring-1 ring-black/[0.03] shadow-[0_8px_24px_rgba(0,0,0,0.04)] hover:shadow-[0_12px_32px_rgba(0,0,0,0.06)] hover:-translate-y-0.5 transition-all duration-500 select-none",
                          variation.checkerboard && "bg-[repeating-conic-gradient(#d1d5db_0%_25%,#f3f4f6_0%_50%)] bg-[length:10px_10px]"
                        )}
                        style={!variation.checkerboard ? { backgroundColor: variation.bgColor } : undefined}
                        onContextMenu={(e) => e.preventDefault()}
                      >
                        {/* Image */}
                        <div className="aspect-square w-full overflow-hidden relative">
                          {variation.previewUrl && (
                            <img 
                              src={variation.previewUrl}
                              alt={variation.label} 
                              className="w-full h-full object-cover block select-none pointer-events-none"
                              draggable={false}
                              onContextMenu={(e) => e.preventDefault()}
                              style={{ 
                                userSelect: 'none',
                                WebkitUserSelect: 'none',
                                WebkitUserDrag: 'none'
                              } as React.CSSProperties}
                            />
                          )}
                        </div>
                        
                        {/* Label + Download */}
                        <div className={cn(
                          "flex items-center justify-between px-2.5 py-2",
                          variation.id === 'tons-neutros' 
                            ? "bg-black/30 text-white" 
                            : "bg-white"
                        )}>
                          <span className="text-[9px] font-medium truncate">{variation.label}</span>
                          <button 
                            onClick={() => handleVariationDownload(variation)}
                            className={cn(
                              "p-1 rounded-md transition-colors",
                              variation.id === 'tons-neutros' 
                                ? "hover:bg-white/20 text-white" 
                                : "hover:bg-slate-100 text-foreground"
                            )}
                          >
                            <Download className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>

{/* CTA Actions - Dashboard + Mascote Cross-sell */}
                <div className="flex flex-col sm:flex-row justify-center items-center gap-3 mt-8">
                  {/* Botão A: Ir para Dashboard (Secundário) */}
                  <Button 
                    variant="outline" 
                    size="lg"
                    onClick={() => navigate("/dashboard")}
                    className="w-full sm:w-auto gap-2 border-border text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <Folder className="w-4 h-4" />
                    Ver Meus Projetos
                  </Button>
                  
                  {/* Botão B: Criar Mascote (Destaque) */}
                  <Button 
                    size="lg"
                    onClick={handleClaimFreeMascot}
                    className="w-full sm:w-auto gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-[0_12px_32px_rgba(249,115,22,0.15)]"
                  >
                    <Gift className="w-4 h-4" />
                    Criar Mascote
                    <span className="ml-1 bg-white/20 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">
                      BÔNUS
                    </span>
                  </Button>
                </div>

                {/* Informativo de valor */}
                <p className="text-center text-white/80 text-xs mt-3">
                  🎁 Você ganhou 1 mascote de bônus por ter criado sua marca!
                </p>
            </div>
          )}

          {/* Hard Paywall Modal - Blocks regeneration without payment */}
          <HardPaywallModal 
            open={showHardPaywall} 
            onLogin={handleLoginFromHardPaywall}
          />
        </div>
      </section>

      {/* TRUST BAR - Authority & Scale */}
      {currentStep === 1 && (
        <section className="py-6 sm:py-8 bg-muted/50 border-y border-border/50">
          <div className="container mx-auto px-6 sm:px-8">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6">
              
              {/* Badge 1: Designer Brasileiro */}
              <div className="flex items-center gap-2.5 bg-white px-4 py-2.5 rounded-xl border border-slate-100 shadow-[0_4px_16px_rgba(0,0,0,0.03)]">
                <BadgeCheck className="w-5 h-5 text-primary flex-shrink-0" />
                <span className="font-bold text-sm text-slate-900">
                  IA desenhada por um designer brasileiro
                </span>
              </div>
              
              {/* Badge 2: Escala de Produção */}
              <div className="flex items-center gap-2.5 bg-white px-4 py-2.5 rounded-xl border border-slate-100 shadow-[0_4px_16px_rgba(0,0,0,0.03)]">
                <Zap className="w-5 h-5 text-amber-500 flex-shrink-0" />
                <span className="font-medium text-sm text-slate-700">
                  +3.000 artes geradas por semana
                </span>
              </div>
              
            </div>
          </div>
        </section>
      )}

      {/* SECTION 2: O QUE VOCÊ RECEBE - Lazy loaded with fixed height fallback */}
      {currentStep === 1 && (
        <Suspense fallback={<div className="min-h-[420px] bg-muted/30" />}>
          <WhatYouGetSection />
        </Suspense>
      )}

      {/* SECTION 3: PRODUCTS - Lazy loaded with fixed height fallback */}
      {currentStep === 1 && (
        <Suspense fallback={<div className="min-h-[600px] bg-muted/30" />}>
          <ProductsSection />
        </Suspense>
      )}

      {/* LEGACY CONTENT - BELOW THE FOLD */}
      {currentStep === 1 && (
        <>
          {/* Social Proof */}
          <LogoGallery />
          <SocialProof />

          {/* Pricing Section */}
          <section className="py-16 sm:py-20 lg:py-28 px-5 sm:px-6 bg-muted/30">
            <div className="container mx-auto">
            <div className="text-center mb-12 sm:mb-16">
                <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
                  <Rocket className="h-4 w-4" />
                  <span className="text-sm font-medium">Criou, Pagou, Baixou.</span>
                </div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-foreground mb-3 leading-tight tracking-tight text-balance">
                  Nossos Planos
                </h2>
                <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
                  Pagamento único. Download imediato.<br />
                  Sem mensalidades. Sem mistérios.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 lg:gap-8 max-w-6xl mx-auto items-stretch px-4 sm:px-0">
                {plans.map((plan, index) => (
                  <Card 
                    key={index} 
                    className={cn(
                      "p-6 sm:p-8 relative transition-all duration-300",
                      "bg-secondary/30 border border-border/50 rounded-xl",
                      "hover:bg-secondary/50",
                      plan.popular && "border-2 border-primary bg-secondary/50",
                      !plan.available && "opacity-60"
                    )}
                  >
                    {/* Badge "Recomendado" */}
                    {plan.popular && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                        <span className="bg-primary text-primary-foreground px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wide whitespace-nowrap shadow-lg">
                          Recomendado
                        </span>
                      </div>
                    )}

                    <div className="text-center mb-8 mt-4">
                      <h3 className="text-xl sm:text-2xl font-bold mb-4 tracking-tight">{plan.name}</h3>
                      {plan.originalPrice && (
                        <div className="text-base text-slate-400 line-through mb-1">
                          {plan.originalPrice}
                        </div>
                      )}
                      <div className="flex items-baseline justify-center gap-1.5 mb-3">
                        <span className="text-3xl sm:text-4xl font-bold tracking-tight">{plan.price}</span>
                        {plan.period && <span className="text-sm text-muted-foreground">/{plan.period}</span>}
                      </div>
                      {plan.discount && (
                        <div className="inline-block bg-gradient-to-r from-amber-400 to-orange-500 text-white px-4 py-1.5 rounded-full text-xs font-bold shadow-[0_4px_12px_rgba(0,0,0,0.03)]">
                          {plan.discount}
                        </div>
                      )}
                    </div>

                    <ul className="space-y-3 mb-8 text-left">
                      {plan.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-start gap-3">
                          <Check className="w-4 h-4 text-primary shrink-0 mt-0.5 stroke-[2]" />
                          <span className="text-sm text-foreground/80 leading-relaxed">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {'bonus' in plan && plan.bonus && (
                      <div className="flex items-center gap-2.5 mb-6 p-3.5 bg-primary/5 border border-primary/10 rounded-xl">
                        <Gift className="w-4 h-4 text-primary shrink-0" />
                        <span className="text-sm font-medium text-primary/90">{plan.bonus}</span>
                      </div>
                    )}

                    <Button 
                      className={cn(
                        "w-full h-14 text-base font-semibold transition-all duration-200 rounded-xl",
                        plan.popular 
                          ? "bg-gradient-to-r from-primary to-primary/80 text-primary-foreground shadow-lg hover:shadow-xl hover:scale-[1.02]" 
                          : "bg-white border-2 border-slate-200 text-foreground hover:border-primary hover:bg-primary/5"
                      )}
                      disabled={!plan.available}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    >
                      {plan.buttonText}
                    </Button>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          <FAQ />

          {/* Footer */}
          <Footer />

          {/* WhatsApp Button */}
          <a
            href="https://wa.me/5511999999999?text=Ol%C3%A1%2C%20vim%20pelo%20ClickLogos%20e%20gostaria%20de%20saber%20mais!"
            target="_blank"
            rel="noopener noreferrer"
            className="fixed bottom-6 right-6 z-50 bg-[#25D366] hover:bg-[#128C7E] text-white p-4 rounded-full shadow-lg transition-colors"
            aria-label="Fale conosco no WhatsApp"
          >
            <MessageCircle className="w-6 h-6" />
          </a>
        </>
      )}

      {/* Signup Modal */}
      <SignupModal 
        open={showSignupModal} 
        onOpenChange={setShowSignupModal}
        brandName={formData.brandName}
        onBeforeOAuthRedirect={handleBeforeOAuthRedirect}
        onSuccess={() => {
          // After successful login/signup, refetch purchase status
          refetchStatus();
          
          // 🆕 Execute pending approval action if any
          if (pendingApprovalAction) {
            const action = pendingApprovalAction;
            setPendingApprovalAction(null);
            // Small delay to allow session to be established
            setTimeout(() => {
              handleApprovalAction(action);
            }, 500);
          }
        }}
      />

      {/* Paywall Modal - For logged in users who haven't paid */}
      <PaywallModal
        open={showPaywall}
        productType="brand_kit"
        previewImageUrl={rawLogoUrl || generatedBrand?.logo?.url}
        onPaymentSuccess={() => {
          setShowPaywall(false);
          refetchStatus();
          toast.success("Pagamento confirmado! 🎉");
          
          // 🆕 Execute pending approval action if any
          if (pendingApprovalAction) {
            const action = pendingApprovalAction;
            setPendingApprovalAction(null);
            // Small delay to allow session to be established
            setTimeout(() => {
              handleApprovalAction(action);
            }, 500);
          } else if (pendingDownload) {
            // Execute pending download using CLEAN URL (no watermark)
            executeActualDownload(pendingDownload.cleanUrl, pendingDownload.id, pendingDownload.bgColor);
            setPendingDownload(null);
          } else {
            // Hero logo download was pending - use CLEAN URL
            executeActualDownload(heroCleanUrl || generatedBrand?.logo?.url, 'logo');
          }
        }}
      />
    </main>
  );
};

export default Index;
